import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import throttle from 'lodash/throttle';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useListZones } from 'src/features/zone/api/useListZones';
import { PERMISSION } from 'src/features/auth/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { LIST_GATEWAY_PASSES_RESOURCE, useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import { CellActiveStaysTableActions } from 'src/features/gatewayPass/components/cell/CellActiveStaysTableActions';
import GatewayPassSidePanel from 'src/features/gatewayPass/components/GatewayPassSidePanel';
import TranslatedName from 'src/features/gatewayPass/components/TranslatedName';
import ZoneTranslation from 'src/features/gatewayPass/components/ZoneTranslation';
import { GatewayPass } from 'src/features/gatewayPass/types';
import { GATEWAY_PASSES_PANEL, visitorTypeBadgeOptions } from 'src/features/gatewayPass/utils/constants';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import { gatewayMessages } from 'src/features/gatewayPass/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import { CellDatetime, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { STAY_TYPE } from 'src/features/stay/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent } from 'src/features/visitor/types';
import { GATEWAY_PASS_ONLY_EVENTS } from 'src/features/visitor/utils/wsEvents';
import { queryClient } from 'src/lib/react-query';
import { commonMessages } from 'src/utils/messages';

type GatewayPassesTableProps = {
    isHomeScreen?: boolean;
    visitor?: string;
    membership?: string;
    stayType?: STAY_TYPE;
    entityId?: string;
    enableSearchParams?: boolean;
};

const columnHelper = createColumnHelper<GatewayPass>();
const initialSortingOptions = { initialSortingState: [{ id: 'time', desc: true }] };
const initialSearchOptions = { globalFilterKey: 'search' };

const GatewayPassesTable = ({
    isHomeScreen,
    visitor,
    stayType,
    membership,
    entityId,
    enableSearchParams = false,
}: GatewayPassesTableProps) => {
    const { formatMessage, locale } = useIntl();
    const { stayId = '' } = useParams();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(initialSortingOptions);
    const { querySearchOptions, ...search } = useSearch(initialSearchOptions);
    const { queryFiltersOptions, ...filters } = useFilters();
    const accessActiveStaysPermission = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStaysPermission = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessVisitorsPermission = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        const handleWebSocket = throttle((event: WebSocketEvent) => {
            if (GATEWAY_PASS_ONLY_EVENTS.includes(event.message)) {
                void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAY_PASSES_RESOURCE] });
            }
        }, 500);
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => {
            handleWebSocket.cancel();
            unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
        };
    }, [subscribe, unsubscribe]);

    const { parkingId } = useParking();

    const {
        data: gatewayPassesData,
        isLoading,
        isError,
    } = useListGatewayPasses({
        parkingId,
        ...(!isHomeScreen && stayType === STAY_TYPE.ACTIVE && { active_stay: stayId }),
        ...(!isHomeScreen && stayType === STAY_TYPE.PAST && { past_stay: stayId }),
        ...(entityId ? { visitor_owner: entityId } : {}),
        ...(visitor && { visitor }),
        ...(membership && { membership }),
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        ...queryFiltersOptions,
    });

    const { data: zoneListData } = useListZones({
        parkingId,
    });
    const { data: gatewayListData } = useListGateways({
        parkingId,
    });
    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();

    const singleRowSelection = React.useCallback(
        (rowId: string) => {
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    const zoneFilterOptions = React.useMemo(
        () =>
            zoneListData?.results.map((zone) => {
                const translatedZone = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedZone?.name };
            }),
        [zoneListData?.results, locale],
    );

    const gatewayFilterOptions = React.useMemo(
        () =>
            gatewayListData?.results.map((zone) => {
                const translatedZone = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedZone?.name };
            }),
        [gatewayListData?.results, locale],
    );

    const gatewayPassId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!gatewayPassesData || !firstSelected) return '';
        return gatewayPassesData.results.find((row) => row.id === firstSelected)?.id;
    }, [gatewayPassesData, rowSelectionState]);

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columns = [
        columnHelper.accessor('time', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                inputType: INPUT_TYPE.DATE,
                title: formatMessage(gatewayMessages.time),
                dateFilter: defaultDateFilter,
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => (
                <CellAction
                    {...props}
                    sidePanelName={GATEWAY_PASSES_PANEL}
                    singleRowSelection={singleRowSelection}
                    render={() => (
                        <span className="text-primary-600 font-normal">{`${formatTimestampToCustomId(props.row.original.time)}`}</span>
                    )}
                />
            ),
            meta: {
                title: formatMessage(commonMessages.refNumber),
            },
        }),
        ...(!visitor
            ? [
                  columnHelper.display({
                      id: 'visitor',
                      header: DataTableColumnHeader,
                      enableSorting: true,
                      enableHiding: true,
                      cell: (props) => {
                          const visitor = props.row.original.visitor;
                          if (!visitor) return '-';
                          const value = `${visitor.main_identifier.value} (${formatMessage(visitorTypeBadgeOptions[visitor.type].label).toLowerCase()}) ${visitor.owner ? `- ${visitor.owner.name}` : ''}`;
                          return (
                              <LinkWithIcon
                                  to={`/${PATH.VISITORS}/visitor/${visitor.id}`}
                                  linkTitle={value}
                                  hasPermissions={accessVisitorsPermission}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(gatewayMessages.visitor),
                      },
                  }),
              ]
            : []),

        ...(isHomeScreen
            ? [
                  columnHelper.accessor('active_stay', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: ({ row }) => {
                          const activeStayRef = row.original.active_stay?.ref;
                          const pastStayRef = row.original.past_stay?.ref;
                          const refToShow = activeStayRef ? activeStayRef : (pastStayRef ?? '-');

                          const activeStayId = row.original.active_stay?.id;
                          const pastStayId = row.original.past_stay?.id;

                          const urlPath = activeStayId
                              ? `active-stay/dashboard/${activeStayId}`
                              : pastStayId
                                ? `past-stay/dashboard/${pastStayId}`
                                : null;

                          const stayPermission = activeStayRef
                              ? accessActiveStaysPermission
                              : accessPastStaysPermission;

                          return urlPath ? (
                              <LinkWithIcon to={urlPath} linkTitle={refToShow} hasPermissions={stayPermission} />
                          ) : (
                              (refToShow ?? '-')
                          );
                      },
                      meta: {
                          title: formatMessage(gatewayMessages.parkingStay),
                      },
                      minSize: 160,
                  }),
              ]
            : []),

        columnHelper.accessor('gateway', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => <TranslatedName translations={props.row.original.gateway?.translations || []} />,
            meta: {
                options: gatewayFilterOptions,
                title: formatMessage(gatewayMessages.gateway),
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('identifiers', {
            header: DataTableColumnHeader,
            // Not sortable: identifiers is a many-to-many list (no single value to order by), matching the
            // active/past stays tables. The backend deliberately exposes no "identifiers" ordering field
            enableHiding: true,
            cell: ({ row }) => {
                const identifiers = row.original?.identifiers?.map((identifier) => identifier.value).join(', ') ?? '-';
                return identifiers;
            },
            meta: {
                title: formatMessage(gatewayMessages.identifiers),
            },
        }),
        columnHelper.accessor('exit_zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => <ZoneTranslation translations={props.row.original.exit_zone?.translations || []} />,
            meta: {
                options: zoneFilterOptions,
                title: formatMessage(gatewayMessages.exitZone),
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('entry_zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => (
                <ZoneTranslation translations={props.row.original.entry_zone?.translations || []} isEntry />
            ),
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(gatewayMessages.entryZone),
                options: zoneFilterOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellActiveStaysTableActions
                    {...props}
                    sidePanelName={GATEWAY_PASSES_PANEL}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
    ];

    return (
        <>
            <DataTable<GatewayPass, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={gatewayPassesData}
                pagination={pagination}
                sorting={sorting}
                search={search}
                filters={filters}
                semantics={{
                    title: formatMessage(gatewayMessages.gatewayPasses),
                    entryName: formatMessage(gatewayMessages.gatewayPass).toLowerCase(),
                    entryNames: formatMessage(gatewayMessages.gatewayPasses).toLowerCase(),
                    sidePanelName: GATEWAY_PASSES_PANEL,
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                enableLocalFilters={false}
                enableSearchParams={enableSearchParams}
            />
            <SidePanel onClose={clearSelection} name={GATEWAY_PASSES_PANEL}>
                <GatewayPassSidePanel gatewayPassId={gatewayPassId} />
            </SidePanel>
        </>
    );
};

export default GatewayPassesTable;
